import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Target } from "lucide-react";
import { ChipInput } from "@/shared/components/ui/Input/chipInput";
import { useMainStore } from "@/stores";
import { fetchWebsiteServices } from "@/api/auth";
import { campaignFormSchema } from "../../../../../../schema/OnBoarding";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import CustomSelect from "@/shared/components/ui/Select/CustomSelect";

import { useGetMyChannels } from "@/hooks/useIntegration";

// HeroUI selects can't select an empty key, so "use the default" gets a sentinel.
const DEFAULT_SENDER_KEY = "__default__";
const toSenderKeys = (value) => new Set([value ? String(value) : DEFAULT_SENDER_KEY]);
const fromSenderKeys = (keys) => {
  const key = [...(keys || [])][0];
  return !key || key === DEFAULT_SENDER_KEY ? "" : String(key);
};

const EMAIL_CHANNELS = new Set(["EMAIL", "MAIL", "IMAP", "GOOGLE_OAUTH", "OUTLOOK"]);
const WHATSAPP_CHANNELS = new Set(["WHATSAPP"]);
const LINKEDIN_CHANNELS = new Set(["LINKEDIN"]);
const EMPTY_INTEGRATIONS = {
  emailChannelAccountId: "",
  linkedinChannelAccountId: "",
  whatsappChannelAccountId: "",
};

const normalize = (value) => String(value || "").trim().toUpperCase();
const accountLabel = (account) =>
  (EMAIL_CHANNELS.has(normalize(account?.channel || account?.provider)) && account?.email)
    || (WHATSAPP_CHANNELS.has(normalize(account?.channel || account?.provider)) && account?.phone)
    || (LINKEDIN_CHANNELS.has(normalize(account?.channel || account?.provider)) && account?.username && `@${account.username.replace(/^@/, "")}`)
    || account?.label
    || account?.email
    || account?.provider
    || account?.channel
    || "Account";

const CampaignForm = ({
  detailsOnly = false,
  onFormDataChange,
  initialValues = { campaignName: "", selectedServices: [] },
  onFormValidationRef,
  isEditMode = false,
}) => {
  const { companyProfile, setCompanyProfile } = useMainStore();
  const services = companyProfile?.company_services || [];
  const { data: channelsData } = useGetMyChannels();
  const [senderValues, setSenderValues] = useState({
    ...EMPTY_INTEGRATIONS,
    ...(initialValues.integrations || {}),
  });

  const channelOptions = useMemo(() => {
    const channels = Array.isArray(channelsData?.result) ? channelsData.result : [];
    const active = channels.filter((account) => normalize(account.status) === "ACTIVE");
    return {
      email: active.filter((account) =>
        EMAIL_CHANNELS.has(normalize(account.channel)) || EMAIL_CHANNELS.has(normalize(account.provider)),
      ),
      linkedin: active.filter((account) =>
        normalize(account.channel) === "LINKEDIN" || normalize(account.provider) === "LINKEDIN",
      ),
      whatsapp: active.filter((account) =>
        normalize(account.channel) === "WHATSAPP" || normalize(account.provider) === "WHATSAPP",
      ),
    };
  }, [channelsData?.result]);

  useEffect(() => {
    if (services.length > 0) return;
    let cancelled = false;
    fetchWebsiteServices()
      .then((data) => {
        if (cancelled) return;
        const fetched = data?.services || [];
        if (fetched.length > 0) {
          setCompanyProfile({ company_services: fetched });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      campaignName: initialValues.campaignName || "",
      selectedServices: initialValues.selectedServices || [],
    },
  });

  // Expose validation trigger to parent
  useEffect(() => {
    if (onFormValidationRef) {
      onFormValidationRef.current = {
        trigger: async () => {
          return await trigger();
        },
        errors,
      };
    }
  }, [trigger, errors, onFormValidationRef]);

  const isResettingRef = useRef(false);
  const prevInitialValuesRef = useRef(initialValues);

  // Update form when initialValues change
  useEffect(() => {
    const hasChanged =
      prevInitialValuesRef.current.campaignName !==
        initialValues.campaignName ||
      prevInitialValuesRef.current.selectedServices.length !==
        initialValues.selectedServices.length ||
      prevInitialValuesRef.current.selectedServices.some(
        (s, i) => s !== initialValues.selectedServices[i],
      );

    if (hasChanged) {
      prevInitialValuesRef.current = initialValues;
      isResettingRef.current = true;
      reset({
        campaignName: initialValues.campaignName || "",
        selectedServices: initialValues.selectedServices || [],
      });
      // Reset flag after reset completes
      setTimeout(() => {
        isResettingRef.current = false;
      }, 0);
    }
  }, [initialValues, reset]);

  useEffect(() => {
    const nextSenderValues = {
      ...EMPTY_INTEGRATIONS,
      ...(initialValues.integrations || {}),
    };
    setSenderValues((current) => {
      const isDifferent = Object.keys(EMPTY_INTEGRATIONS).some(
        (key) => current[key] !== nextSenderValues[key],
      );
      return isDifferent ? nextSenderValues : current;
    });
  }, [initialValues.integrations]);

  const campaignName = watch("campaignName");
  const selectedServices = watch("selectedServices");

  // Notify parent of form changes
  useEffect(() => {
    if (!isResettingRef.current && onFormDataChange) {
      onFormDataChange({
        campaignName: campaignName?.trim() || "",
        selectedServices: selectedServices || [],
        integrations: senderValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, selectedServices, senderValues]);

  const updateSender = (field, value) => {
    setSenderValues((current) => ({ ...current, [field]: value || "" }));
  };

  return (
    <div className="w-full flex flex-col gap-9">
      {/* form */}
      <div
        className={`flex flex-col gap-7 px-0.5 overflow-y-auto ${
          detailsOnly
            ? "max-h-[min(520px,calc(100dvh-320px))]"
            : "max-h-[450px]"
        }`}
      >
        {/* campaign name */}
        <div className="flex flex-col gap-3.5">
          <div className="text-[0.9375rem] font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <FileText size={18} className="text-gray-500 dark:text-gray-400" strokeWidth={1.7} />
            Campaign Name
          </div>
          <CommonInput
            label="Campaign Name"
            placeholder="Enter a suitable campaign title here"
            value={campaignName}
            onChange={(e) => setValue("campaignName", e.target.value)}
            error={errors.campaignName}
          />
          {errors.campaignName && (
            <p className="text-error-500 text-xs mt-1">
              {errors.campaignName.message}
            </p>
          )}
        </div>

        {/* services */}
        <div className="flex flex-col gap-3.5">
          <div className="text-[0.9375rem] font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Target size={18} className="text-gray-500 dark:text-gray-400" strokeWidth={1.7} />
            Which service are you looking to sell through this campaign?
          </div>
          <ChipInput
            defaultChips={isEditMode ? initialValues.selectedServices : []}
            placeholder="Select from suggestion or type new & press enter to add it!"
            dropdownOptions={services}
            onChange={(newServices) => {
              setValue("selectedServices", newServices, {
                shouldValidate: true,
              });
            }}
            className="mb-1"
          />
          {errors.selectedServices && (
            <p className="text-error-500 text-xs mt-1">
              {errors.selectedServices.message}
            </p>
          )}
        </div>

        {!isEditMode && (
          <div className="flex flex-col gap-3.5 rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <div>
              <div className="text-[0.9375rem] font-semibold text-gray-800 dark:text-gray-100">
                Choose campaign senders <span className="font-normal text-gray-500">(optional)</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Connect accounts once in Settings, then choose a specific account for this campaign. Blank fields use the active default.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                { key: "emailChannelAccountId", label: "Email sender", items: channelOptions.email, empty: "Use active default email" },
                { key: "linkedinChannelAccountId", label: "LinkedIn sender", items: channelOptions.linkedin, empty: "Use active default LinkedIn" },
                { key: "whatsappChannelAccountId", label: "WhatsApp sender", items: channelOptions.whatsapp, empty: "Use active default WhatsApp" },
              ].map((field) => (
                <CustomSelect
                  key={field.key}
                  label={field.label}
                  options={[
                    { key: DEFAULT_SENDER_KEY, title: field.empty },
                    ...field.items.map((account) => ({ key: String(account.id), title: accountLabel(account) })),
                  ]}
                  selectedKeys={toSenderKeys(senderValues[field.key])}
                  onSelectionChange={(keys) => updateSender(field.key, fromSenderKeys(keys))}
                  showDescription={false}
                  size="sm"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignForm;
